import {useAuth} from '@apps-common/auth-react';
import {NotificationsMenu} from '@apps-common/notifications';
import AppsDropdown from '@apps-common/ui-components/NavBar/AppsDropdown.jsx';
import HelpMenu from '@apps-common/ui-components/NavBar/HelpMenu.jsx';
import NavBar from '@apps-common/ui-components/NavBar/NavBar.jsx';
import {NavButton} from '@apps-common/ui-components/NavBar/NavMenuButton.jsx';
import SettingsSidebar from '@components/sidebars/settings/SettingsSidebar.jsx';
import SettingsIcon from '@mui/icons-material/Settings';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import {pink, purple} from '@mui/material/colors';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import {reset as resetAnalytics, trackEvent} from '@src/analytics';
import API_URLS from '@src/api/urls.ts';
import {
  CHAMELEON_SUPERRES_TOUR_ID,
  DEPLOY_ENV,
  NOTIFICATIONS_ENV,
} from '@src/config.ts';
import {isAnalyzeBuild} from '@src/constants/routes';
import {useFlags} from '@src/hooks/useFlags.js';
import useMapMode from '@src/hooks/useMapMode.js';
import {useCallback, useState} from 'react';

const EnvironmentChip = () => {
  if (
    window.location.hostname === 'www.planet.com' ||
    window.location.hostname === 'insights.planet.com'
  ) {
    return false;
  }

  const label = import.meta.env.DEV
    ? 'Local'
    : window.location.pathname.startsWith('/acorns/live/main')
      ? 'Internal'
      : 'Branch';

  const tooltipText = `API base: ${API_URLS.base}`;

  return (
    <Tooltip title={tooltipText}>
      <Chip
        label={label}
        shape="square"
        size="small"
        sx={{
          backgroundColor: import.meta.env.DEV ? purple[500] : pink[500],
        }}
      />
    </Tooltip>
  );
};

const TopNav = () => {
  const auth = useAuth();
  const {notificationsInPlatformApps} = useFlags();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const {isBrowserMode} = useMapMode();

  const handleLogout = useCallback(() => {
    trackEvent('Logout Button clicked');

    resetAnalytics();

    auth.signOut();
  }, [auth]);

  const showSuperResTourItem =
    isBrowserMode &&
    document.querySelector('[data-chameleon="super-res-available"]') !== null;

  const appKey = isAnalyzeBuild ? 'analyze' : 'data';

  return (
    <>
      <NavBar
        title={<AppsDropdown appKey={appKey} environment={DEPLOY_ENV} />}
        user={auth.user?.claims}
        secondary={
          <>
            <Box sx={{marginInlineEnd: 1.2}}>
              <EnvironmentChip />
            </Box>

            {notificationsInPlatformApps && (
              <NotificationsMenu env={NOTIFICATIONS_ENV} />
            )}

            <HelpMenu>
              {showSuperResTourItem && (
                <MenuItem
                  onClick={() => {
                    window.chmln?.show(CHAMELEON_SUPERRES_TOUR_ID);
                  }}
                >
                  Take a SuperRes tour!
                </MenuItem>
              )}
            </HelpMenu>

            <NavButton
              aria-label="Settings"
              onClick={() => setSettingsOpen(true)}
            >
              <SettingsIcon fontSize="small" />
            </NavButton>
          </>
        }
        logout={handleLogout}
        sx={{zIndex: 'modal'}}
        environment={DEPLOY_ENV}
        className="pl-mode-dark"
      />

      <SettingsSidebar
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </>
  );
};

export default TopNav;
